export const OPENPENCIL_FILL_HEX_PATTERN = /^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/;

export interface OpenPencilRgbColor {
  r: number;
  g: number;
  b: number;
  a?: number;
}

export interface OpenPencilSolidFillLike {
  type: string;
  color: OpenPencilRgbColor;
  opacity?: number;
}

export function normalizeOpenPencilFillHex(hex: string): string {
  const normalized = hex.trim().toLowerCase();
  if (!OPENPENCIL_FILL_HEX_PATTERN.test(normalized)) {
    throw new Error(
      `Fill colors must be 6- or 8-digit sRGB hex values, received ${JSON.stringify(hex)}.`,
    );
  }
  return normalized;
}

/**
 * Parse a 6- or 8-digit hex color. The alpha channel is present in the result
 * ONLY when the request spelled it out (8 digits) — spreading the result over
 * an existing engine color therefore preserves the current alpha for 6-digit
 * requests and sets it explicitly for 8-digit requests.
 */
export function openPencilHexToRgb(hex: string): OpenPencilRgbColor {
  const normalized = normalizeOpenPencilFillHex(hex);
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16) / 255,
    g: Number.parseInt(normalized.slice(3, 5), 16) / 255,
    b: Number.parseInt(normalized.slice(5, 7), 16) / 255,
    ...(normalized.length === 9
      ? { a: Number.parseInt(normalized.slice(7, 9), 16) / 255 }
      : {}),
  };
}

function colorChannelByteHex(value: number): string {
  return Math.min(255, Math.max(0, Math.round(value * 255)))
    .toString(16)
    .padStart(2, "0");
}

export function openPencilColorToHex(color: OpenPencilRgbColor): string {
  return `#${colorChannelByteHex(color.r)}${colorChannelByteHex(color.g)}${colorChannelByteHex(color.b)}`;
}

/**
 * Canonical revision-evidence form: uppercase `#RRGGBB`, with an `AA` suffix
 * only when the color is not fully opaque.
 */
export function openPencilCanonicalFillHex(color: OpenPencilRgbColor): string {
  const rgb = openPencilColorToHex(color).slice(1).toUpperCase();
  const alpha = colorChannelByteHex(color.a ?? 1).toUpperCase();
  return `#${rgb}${alpha === "FF" ? "" : alpha}`;
}

/** Canonical form of a REQUESTED hex value (`#336699ff` → `#336699`). */
export function openPencilCanonicalRequestHex(hex: string): string {
  const parsed = openPencilHexToRgb(hex);
  return openPencilCanonicalFillHex(parsed);
}

export interface OpenPencilFillEditRequest {
  fillIndex: number;
  expectedColor?: string;
  color?: string;
  fillOpacity?: number;
}

/**
 * Plan a bounded solid-fill edit (the single `set-solid-fill` operation).
 *
 * Preconditions enforced here: the fill exists at `fillIndex`, it is SOLID,
 * and — when `expectedColor` is provided — the current color matches it
 * exactly (canonical compare). The variable-binding refusal stays in the
 * runtimes because it needs the node's `boundVariables` table.
 *
 * Evidence keys are conditional on what the request asked for: `color` only
 * when a color change was requested, `fillOpacity` only when a fill-opacity
 * change was requested — so color-only evidence keeps the historical
 * `{ fillIndex, color }` shape byte-stable.
 */
export function planOpenPencilFillChange<F extends OpenPencilSolidFillLike>(
  fills: readonly F[] | undefined,
  request: OpenPencilFillEditRequest,
  nodeLabel: string,
): {
  nextFills: F[];
  before: Record<string, string | number>;
  after: Record<string, string | number>;
} {
  const target = fills?.[request.fillIndex];
  if (!target) {
    throw new Error(
      `Node ${nodeLabel} has no fill at index ${request.fillIndex}.`,
    );
  }
  if (target.type !== "SOLID") {
    throw new Error(
      `Node ${nodeLabel} fill ${request.fillIndex} is ${target.type}, not SOLID.`,
    );
  }
  const currentColor = openPencilCanonicalFillHex(target.color);
  if (
    request.expectedColor !== undefined &&
    currentColor !== openPencilCanonicalRequestHex(request.expectedColor)
  ) {
    throw new Error(
      `Node ${nodeLabel} fill ${request.fillIndex} color changed: expected ${openPencilCanonicalRequestHex(request.expectedColor)}, found ${currentColor}.`,
    );
  }
  const nextFill = { ...target };
  if (request.color !== undefined) {
    // 6-digit requests preserve the fill's current alpha channel; 8-digit
    // requests carry an explicit alpha and set it.
    nextFill.color = { ...target.color, ...openPencilHexToRgb(request.color) };
  }
  if (request.fillOpacity !== undefined) {
    nextFill.opacity = request.fillOpacity;
  }
  const nextFills = (fills ?? []).map((fill, index) =>
    index === request.fillIndex ? nextFill : fill,
  );
  return {
    nextFills,
    before: {
      fillIndex: request.fillIndex,
      ...(request.color !== undefined ? { color: currentColor } : {}),
      ...(request.fillOpacity !== undefined
        ? { fillOpacity: target.opacity ?? 1 }
        : {}),
    },
    after: {
      fillIndex: request.fillIndex,
      ...(request.color !== undefined
        ? { color: openPencilCanonicalRequestHex(request.color) }
        : {}),
      ...(request.fillOpacity !== undefined
        ? { fillOpacity: request.fillOpacity }
        : {}),
    },
  };
}

/**
 * Semantic-readback observation for a reopened SOLID fill, keyed off the
 * evidence `after` record so observed keys always mirror the requested keys.
 * A 6-digit `after.color` is compared at RGB precision (the request preserved
 * the alpha channel, so the observed alpha is not part of the contract); an
 * 8-digit `after.color` is compared at full RGBA precision.
 */
export function observedOpenPencilSolidFill(
  fill: OpenPencilSolidFillLike,
  after: Record<string, string | number | boolean | null>,
): Record<string, string | number> {
  const observed: Record<string, string | number> = {
    fillIndex: typeof after.fillIndex === "number" ? after.fillIndex : -1,
  };
  if ("color" in after) {
    const canonical = openPencilCanonicalFillHex(fill.color);
    observed.color =
      typeof after.color === "string" && after.color.length === 7
        ? canonical.slice(0, 7)
        : canonical;
  }
  if ("fillOpacity" in after) {
    observed.fillOpacity = fill.opacity ?? 1;
  }
  return observed;
}
